import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonChip,
  IonCol,
  IonIcon,
  IonItem,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  IonToggle,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { closeCircleOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IOption } from '../../../common/common.types'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import { getInsuranceInformationOptionsSelector } from '../../options/selectors/getInsuranceInformationOptions.selector'
import { getInsuranceInformationStateSelector } from '../selectors/insuranceInformationState.selector'
import {
  setGroupname,
  setInsurancecarrier,
  setPolicy,
  setPriority,
  setActive,
  setInsured,
  removeInsuranceInformation,
  setGroupNumber,
  addInsuranceInformation,
  clearInsuranceInformationData,
  setInsuranceInformationData,
  saveInsuranceInformation,
} from '../store/insuranceInformation.actions'
import { InsuranceInformationConstants } from '../store/insuranceInformation.constants'
import { initialInsuranceInformation } from '../store/insuranceInformation.reducers'
import { getRulesSelector } from '../../rules/selectors'
import { IRuleError, IRulesState } from '../../rules/store/rules.types'
import { IInsuranceInformation } from '../store/insuranceInformation.types'
import { clearValidationErrorsByCardinalityKey } from '../../../utilities/runRulesValidation.util'

const InsuranceInformationContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const rules: IRulesState = useSelector(getRulesSelector)
  const insuranceInformationState = useSelector(getInsuranceInformationStateSelector)
  const { insuranceInformationData, insuranceInformationList } = insuranceInformationState
  const { insuranceCarrier, groupName, groupNumber, policy, insured, priority, active } = insuranceInformationData
  const insuranceInformationOptionsState = useSelector(getInsuranceInformationOptionsSelector)
  const { priorityOptions, insuredOptions } = insuranceInformationOptionsState
  const [flag, setFlag] = useState(true)
  const isDataBlank = JSON.stringify(insuranceInformationData) === JSON.stringify(initialInsuranceInformation)
  const rulesState = useSelector(getRulesSelector)
  const { errors } = rulesState
  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  useEffect(() => {
    if (flag) {
      insuranceCarrier && dispatch(setInsurancecarrier(insuranceCarrier))
      groupNumber && dispatch(setGroupNumber(groupNumber))
      groupName && dispatch(setGroupname(groupName))
      policy && dispatch(setPolicy(policy))
    }
  }, [flag])

  const setInsuranceCarrierOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setInsurancecarrier(e.detail.value))
  }

  const setGroupNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setGroupname(e.detail.value))
  }

  const setGroupNumberOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setGroupNumber(e.detail.value))
  }

  const setPolicyOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPolicy(e.detail.value))
  }

  const setPriorityOnChange = (option: IOption) => {
    dispatch(setPriority(option))
  }

  const setInsuredOnChange = (option: IOption) => {
    dispatch(setInsured(option))
  }
  const activeOnChange = (e: CustomEvent) => {
    dispatch(setActive(e.detail.checked))
  }

  const onAddInsuranceInformation = () => {
    const reduxActions: string[] = [
      InsuranceInformationConstants.SET_INSURANCE_CARRIER,
      InsuranceInformationConstants.SET_GROUP_NAME,
      InsuranceInformationConstants.SET_GROUP_NUMBER,
      InsuranceInformationConstants.SET_POLICY,
      'state_insuranceInformation/SET_INSURANCE_CARRIER',
      'state_insuranceInformation/SET_GROUP_NAME',
      'state_insuranceInformation/SET_GROUP_NUMBER',
      'state_insuranceInformation/SET_POLICY',
    ]
    const validationErrors = Object.values(errors)
      .flat()
      .filter((error: IRuleError) => reduxActions.includes(error.id) && error.reduxStateOnlyFlag)

    if (validationErrors.length) {
      alert('Please Correct Errors')
      return
    }

    if (insuranceCarrier) {
      dispatch(addInsuranceInformation())
      dispatch(setInsurancecarrier(''))
      dispatch(setPolicy(''))
      dispatch(setGroupNumber(''))
      dispatch(setGroupname(''))
    } else {
      alert('Insurance Carrier Required')
    }
  }

  const onClearInsuranceInformation = () => {
    dispatch(setInsurancecarrier(''))
    dispatch(setPolicy(''))
    dispatch(setGroupNumber(''))
    dispatch(setGroupname(''))
    dispatch(clearInsuranceInformationData())
    dispatch(saveInsuranceInformation())
  }

  const handleChipClick = (e: BaseSyntheticEvent, insuranceInfo: IInsuranceInformation) => {
    e.stopPropagation()
    const insuranceInformationData = insuranceInformationList.find((iInfo) => iInfo.key === insuranceInfo.key)
    if (insuranceInformationData) {
      dispatch(setInsurancecarrier(insuranceInformationData.insuranceCarrier))
      dispatch(setPolicy(insuranceInformationData.policy))
      dispatch(setGroupNumber(insuranceInformationData.groupNumber))
      dispatch(setGroupname(insuranceInformationData.groupName))

      dispatch(setInsuranceInformationData(insuranceInformationData))
      rules.errors = clearValidationErrorsByCardinalityKey(rules.errors, insuranceInfo.key)
    }
  }

  const handleRemoveChip = (e: BaseSyntheticEvent, key: string) => {
    e.stopPropagation()
    dispatch(removeInsuranceInformation(key))
    rules.errors = clearValidationErrorsByCardinalityKey(rules.errors, key)
  }

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('insuranceInformation.addInsuranceDetails')}
        </IonLabel>
      </IonItemDivider>
      <IonRow className='ion-padding-start'>
        {insuranceInformationList &&
          insuranceInformationList.map((insuranceInfo, index) => (
            <IonChip key={insuranceInfo.key} onClick={(e) => handleChipClick(e, insuranceInfo)}>
              <IonLabel className='label-wrap'>
                {insuranceInfo.insuranceCarrier || insuranceInfo.companyCoded?.name}
              </IonLabel>
              <IonIcon
                date-testid={`clear_filter_icon_${index}`}
                class='ion-align-self-center'
                size='small'
                icon={closeCircleOutline}
                color='Contrast'
                onClick={(e) => handleRemoveChip(e, insuranceInfo.key)}
              />
            </IonChip>
          ))}
      </IonRow>

      <IonList>
        <IonRow>
          <IonCol className='picklist'>
            <SInput
              label={t('insuranceInformation.insuranceCarrier')}
              dataTestID='InsuranceCarrier'
              inputValue={insuranceCarrier}
              onChangeAction={setInsuranceCarrierOnChange}
            />
            <Validation id={InsuranceInformationConstants.SET_INSURANCE_CARRIER} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SInput
              label={t('insuranceInformation.groupName')}
              dataTestID='GroupName'
              inputValue={groupName}
              onChangeAction={setGroupNameOnChange}
            />
            <Validation id={InsuranceInformationConstants.SET_GROUP_NAME} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SInput
              label={t('insuranceInformation.groupNumber')}
              dataTestID='GroupNumber'
              inputValue={groupNumber}
              onChangeAction={setGroupNumberOnChange}
            />
            <Validation id={InsuranceInformationConstants.SET_GROUP_NUMBER} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SInput
              label={t('insuranceInformation.policy')}
              dataTestID='Policy'
              inputValue={policy}
              onChangeAction={setPolicyOnChange}
            />
            <Validation id={InsuranceInformationConstants.SET_POLICY} showReduxStateErrorsOnlyFlag={true} />
          </IonCol>

          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Priority'
              options={priorityOptions}
              selectedOption={priority}
              listHeader={t('insuranceInformation.priority')}
              label={t('insuranceInformation.priority')}
              action={setPriorityOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Insured'
              options={insuredOptions}
              selectedOption={insured}
              listHeader={t('insuranceInformation.insured')}
              label={t('insuranceInformation.insured')}
              action={setInsuredOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol>
            <IonItem lines='none'>
              <IonLabel>{t('insuranceInformation.active')}</IonLabel>
              <IonToggle
                slot='start'
                checked={active}
                onIonChange={(e) => {
                  activeOnChange(e)
                }}
              />
            </IonItem>
          </IonCol>
        </IonRow>
      </IonList>
      <IonRow className='ion-float-right '>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              disabled={isDataBlank}
              className='btn btn-standard mr-8'
              color='danger'
              fill='outline'
              expand='block'
              onClick={() => onClearInsuranceInformation()}>
              {t('common.buttons.Clear')}
            </IonButton>
            <IonButton
              disabled={isDataBlank}
              className='btn btn-standard btn-primary'
              expand='block'
              onClick={() => onAddInsuranceInformation()}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonRow>
    </>
  )
}

export default InsuranceInformationContent
